import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import {
  AfterContentInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Injector,
  computed,
  forwardRef,
  inject,
  input,
} from '@angular/core';
import { NgClass } from '@angular/common';
import {
  AbstractControl,
  ControlValueAccessor,
  FormControl,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  NgControl,
  ReactiveFormsModule,
  ValidationErrors,
  Validator,
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { BaseControlValueAccessor } from '../base-control-value-accessor';

@Component({
  selector: 'lib-form-money',
  templateUrl: './form-money.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReactiveFormsModule, MatInputModule, MatFormFieldModule, NgxMaskDirective, NgClass],
  providers: [
    provideNgxMask(),
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => FormMoneyComponent), multi: true },
    { provide: NG_VALIDATORS, useExisting: forwardRef(() => FormMoneyComponent), multi: true },
  ],
})
export class FormMoneyComponent
  extends BaseControlValueAccessor<string>
  implements AfterContentInit, ControlValueAccessor, Validator
{
  private static nextId = 0;
  private injector = inject(Injector);
  private cdRef = inject(ChangeDetectorRef);

  readonly inputId = `form-money-${FormMoneyComponent.nextId++}`;
  label = input<string>('Amount');
  hideRequiredMarker = input<boolean>(false);
  startHint = input<string>();
  endHint = input<string>();
  prefix = input<string>('$');
  autocomplete = input<string>('off');
  min = input<string>('0');
  max = input<string>();
  minError = input<string>('Amount cannot be negative');
  maxError = input<string>();
  allowDecimals = input<boolean>(false);
  readonly = input<boolean>(false);
  alignment = input<'left' | 'center' | 'right'>('left');
  placeholder = input<string>('');
  ariaLabel = input<string>('');

  autocompleteComputed = computed<string>(() =>
    this.autocomplete() === 'off' ? this.inputId : this.autocomplete(),
  );
  maskComputed = computed<string>(() => (this.allowDecimals() ? 'separator.2' : 'separator.0'));
  patternComputed = computed<RegExp>(() => (this.allowDecimals() ? /^\d*\.?\d{0,2}$/ : /^\d+$/));
  patternErrorMessageComputed = computed<string>(() =>
    this.allowDecimals() ? 'Cannot be more than two decimal places' : 'Whole dollars only',
  );

  ngAfterContentInit(): void {
    const ngControl = this.injector.get(NgControl, null);
    this.control = (ngControl?.control as FormControl) ?? new FormControl();
  }

  validate(control: AbstractControl): ValidationErrors | null {
    return control.valid ? null : control.errors;
  }

  // Store a clean number string ("1234.56") whatever shape the model hands us.
  override writeValue(value: number | string | null): void {
    if (value === null || value === undefined) {
      this.value = '';
    } else {
      this.value = typeof value === 'number' ? value.toFixed(2) : value.replace(/[^0-9.]/g, '');
    }
    this.cdRef.markForCheck();
  }

  protected override getLabel(): string {
    return this.label() || '';
  }

  override getErrorMessage(errorKey: string): string {
    switch (errorKey) {
      case 'pattern':
        return this.patternErrorMessageComputed();
      case 'min':
        return this.minError();
      case 'max':
        return this.maxError() || 'Value exceeds maximum';
      default:
        return super.getErrorMessage(errorKey);
    }
  }
}
